if (!customElements.get('restivo-review-pan')) {
  customElements.define('restivo-review-pan', class extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.restivo-reviews');
      this.widget = this.section && this.section.querySelector('#judgeme_product_reviews');
      if (!this.widget) return;
      this.track = this.querySelector('.restivo-pan__track');
      this.button = this.querySelector('.restivo-pan__toggle');
      this.dataset.paused = 'false';
      this.button.addEventListener('click', () => {
        const paused = this.dataset.paused !== 'true';
        this.dataset.paused = String(paused);
        this.button.setAttribute('aria-pressed', String(paused));
        this.button.textContent = paused ? this.dataset.resumeLabel : this.dataset.pauseLabel;
      });
      this.addEventListener('touchstart', () => { this.dataset.touch = 'true'; }, { passive: true });
      this.addEventListener('touchend', () => { this.dataset.touch = 'false'; }, { passive: true });
      this.addEventListener('touchcancel', () => { this.dataset.touch = 'false'; }, { passive: true });
      this.mutations = new MutationObserver(() => {
        clearTimeout(this.refreshTimer);
        this.refreshTimer = setTimeout(() => this.build(), 160);
      });
      this.mutations.observe(this.widget, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['src', 'srcset', 'hidden', 'aria-hidden']
      });
      this.visibility = new IntersectionObserver(entries => {
        this.dataset.visible = String(entries.some(entry => entry.isIntersecting));
      });
      this.visibility.observe(this);
      this.resize = new ResizeObserver(() => {
        this.setDuration();
        this.updateReadMore();
      });
      this.resize.observe(this);
      this.build();
    }
    build() {
      const sort = this.widget.querySelector('.jm-sort-filter__select');
      if (sort && !this.sortInitialized) {
        this.sortInitialized = true;
        if (this.dataset.photosFirst === 'true' && sort.value === 'created_at') {
          sort.value = 'pictures_first';
          sort.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }
      }
      const source = Array.from(this.widget.querySelectorAll('.jdgm-review-list .jm-review-item'));
      const reviews = source.map((card, sourceIndex) => ({
        sourceIndex,
        text: (card.querySelector('.jm-review-content__body')?.textContent || '').trim(),
        name: (card.querySelector('.jm-reviewer-info__name')?.textContent || '').trim(),
        rating: (card.querySelector('.jm-star-rating')?.getAttribute('aria-label') || '').match(/\d+(?:\.\d+)?/)?.[0],
        photos: Array.from(card.querySelectorAll('.jm-media-thumbnail-list__thumbnail img')).flatMap((image, index) => {
          if (image.closest('[hidden], [aria-hidden="true"]')) return [];
          const src = image.getAttribute('src');
          if (!src || !/^https:\/\//i.test(src)) return [];
          return [{ src, srcset: image.getAttribute('srcset') || '', index }];
        }),
        verified: Array.from(card.querySelectorAll('.jm-reviewer-info [class*="verified"], .jm-reviewer-info [class*="badge"]')).some(badge => !String(badge.className).toLowerCase().includes('unverified') && /^(verified|verified buyer|verified customer|verified purchase)$/i.test(badge.textContent.trim()))
      })).filter(review => review.text && review.name && review.rating);
      if (reviews.length < 2) {
        this.hidden = true;
        this.section.classList.remove('restivo-reviews--panning');
        this.signature = '';
        return;
      }
      const signature = JSON.stringify(reviews);
      if (signature === this.signature) return;
      this.signature = signature;
      const group = document.createElement('div');
      group.className = 'restivo-pan__group';
      for (const review of reviews) {
        const card = document.createElement('article');
        card.className = 'restivo-pan__card';
        const stars = document.createElement('div');
        stars.className = 'restivo-pan__stars';
        stars.setAttribute('role', 'img');
        stars.setAttribute('aria-label', this.dataset.ratingLabel.replace('[rating]', review.rating));
        const rating = Math.max(0, Math.min(5, Math.round(Number(review.rating))));
        stars.textContent = '★'.repeat(rating) + '☆'.repeat(5 - rating);
        const body = document.createElement('p');
        body.className = 'restivo-pan__text';
        body.textContent = review.text;
        body.id = `RestivoReview-${this.section.id}-${review.sourceIndex}`;
        const readMore = document.createElement('button');
        readMore.type = 'button';
        readMore.className = 'restivo-pan__read-more';
        readMore.hidden = true;
        readMore.textContent = this.dataset.readMoreLabel;
        readMore.setAttribute('aria-expanded', 'false');
        readMore.setAttribute('aria-controls', body.id);
        readMore.addEventListener('click', () => {
          const expanded = card.dataset.expanded !== 'true';
          card.dataset.expanded = String(expanded);
          readMore.setAttribute('aria-expanded', String(expanded));
          readMore.textContent = expanded ? this.dataset.showLessLabel : this.dataset.readMoreLabel;
        });
        const author = document.createElement('div');
        author.className = 'restivo-pan__author';
        const initial = document.createElement('span');
        initial.className = 'restivo-pan__initial';
        initial.setAttribute('aria-hidden', 'true');
        initial.textContent = Array.from(review.name)[0];
        const name = document.createElement('span');
        name.textContent = review.name;
        const details = document.createElement('div');
        details.className = 'restivo-pan__author-details';
        const badge = document.createElement('span');
        badge.className = 'restivo-pan__badge' + (review.verified ? ' restivo-pan__badge--verified' : '');
        const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        icon.setAttribute('viewBox', '0 0 20 20');
        icon.setAttribute('width', '14');
        icon.setAttribute('height', '14');
        icon.setAttribute('aria-hidden', 'true');
        icon.setAttribute('focusable', 'false');
        icon.setAttribute('fill', 'none');
        icon.setAttribute('stroke', 'currentColor');
        icon.setAttribute('stroke-width', '1.5');
        icon.setAttribute('stroke-linecap', 'round');
        icon.setAttribute('stroke-linejoin', 'round');
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('d', review.verified ? 'M10 2 16 4v5c0 4-6 7-6 7S4 13 4 9V4l6-2Zm-3 7 2 2 4-4' : 'M4 3.5h12a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H9l-5 3v-3a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1ZM6 7h8M6 10h5');
        icon.append(path);
        badge.append(icon, document.createTextNode(review.verified ? this.dataset.verifiedLabel : this.dataset.reviewLabel));
        details.append(name, badge);
        author.append(initial, details);
        card.append(stars, body, readMore);
        if (review.photos.length) {
          const media = document.createElement('div');
          media.className = 'restivo-pan__media';
          for (const photo of review.photos) {
            const trigger = document.createElement('button');
            trigger.type = 'button';
            trigger.className = 'restivo-pan__photo';
            trigger.setAttribute('aria-label', this.dataset.viewPhotoLabel.replace('[name]', review.name));
            const image = document.createElement('img');
            image.className = 'restivo-pan__image';
            image.src = photo.src;
            if (photo.srcset) image.srcset = photo.srcset;
            image.sizes = '(max-width: 599px) calc(100vw - 110px), 292px';
            image.alt = this.dataset.photoLabel.replace('[name]', review.name);
            image.width = 640;
            image.height = 480;
            image.loading = 'lazy';
            image.decoding = 'async';
            trigger.append(image);
            trigger.addEventListener('click', () => {
              const original = source[review.sourceIndex]?.querySelectorAll('.jm-media-thumbnail-list__thumbnail img')[photo.index];
              if (original?.isConnected && !original.closest('[hidden], [aria-hidden="true"]')) {
                original.closest('.jm-media-thumbnail-list__thumbnail')?.click();
              }
            });
            media.append(trigger);
          }
          card.append(media);
        }
        card.append(author);
        group.append(card);
      }
      const duplicate = group.cloneNode(true);
      duplicate.setAttribute('aria-hidden', 'true');
      duplicate.setAttribute('inert', '');
      duplicate.querySelectorAll('[id]').forEach(element => element.removeAttribute('id'));
      duplicate.querySelectorAll('[aria-controls]').forEach(element => element.removeAttribute('aria-controls'));
      this.track.replaceChildren(group, duplicate);
      this.hidden = false;
      this.section.classList.add('restivo-reviews--panning');
      this.setDuration();
      this.updateReadMore();
    }
    updateReadMore() {
      const mobile = window.matchMedia('(max-width: 599px)').matches;
      this.track?.firstElementChild?.querySelectorAll('.restivo-pan__card').forEach(card => {
        const body = card.querySelector('.restivo-pan__text');
        const button = card.querySelector('.restivo-pan__read-more');
        button.hidden = !mobile || (card.dataset.expanded !== 'true' && body.scrollHeight <= body.clientHeight + 1);
      });
    }
    setDuration() {
      const group = this.track?.firstElementChild;
      if (group && group.offsetWidth) {
        this.style.setProperty('--restivo-pan-duration', (group.offsetWidth / 18) + 's');
      }
    }
    disconnectedCallback() {
      clearTimeout(this.refreshTimer);
      this.mutations?.disconnect();
      this.visibility?.disconnect();
      this.resize?.disconnect();
    }
  });
}
